import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import DesignTokens from '../../styles/DesignTokens';
import { SceneBackground, BlueCTAButton } from '../shared/GlassCard';
import TravelDNAResult from './TravelDNAResult';

// --- Mood Board Data ---

const MOOD_CARDS = [
  {
    id: 'temple-sunrise',
    title: 'Sunrise at a Temple',
    image: 'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=600&q=80',
    signals: { culture: 0.8, adventure: 0.2, relaxation: 0.3, food: 0, nature: 0.4, social: 0.1 },
    tags: ['early_riser', 'spiritual', 'cultural'],
  },
  {
    id: 'street-food',
    title: 'Street Food at Night',
    image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80',
    signals: { culture: 0.3, adventure: 0.5, relaxation: 0, food: 0.9, nature: 0, social: 0.6 },
    tags: ['food', 'nightlife', 'budget', 'adventurous'],
  },
  {
    id: 'mountain-hike',
    title: 'Mountain Hiking Trail',
    image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=80',
    signals: { culture: 0, adventure: 0.9, relaxation: 0.2, food: 0, nature: 0.9, social: 0.2 },
    tags: ['adventure', 'nature', 'active'],
  },
  {
    id: 'museum',
    title: 'Museum Gallery',
    image: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=600&q=80',
    signals: { culture: 0.9, adventure: 0.1, relaxation: 0.6, food: 0, nature: 0, social: 0.3 },
    tags: ['culture', 'history', 'relaxed'],
  },
  {
    id: 'beach-sunset',
    title: 'Beach Sunset',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80',
    signals: { culture: 0, adventure: 0.2, relaxation: 0.9, food: 0, nature: 0.8, social: 0.3 },
    tags: ['relaxation', 'nature', 'photography'],
  },
  {
    id: 'local-market',
    title: 'Local Market',
    image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=80',
    signals: { culture: 0.7, adventure: 0.3, relaxation: 0.1, food: 0.7, nature: 0, social: 0.8 },
    tags: ['food', 'cultural', 'shopping', 'social'],
  },
  {
    id: 'rooftop-bar',
    title: 'Rooftop Bar View',
    image: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=600&q=80',
    signals: { culture: 0.1, adventure: 0.2, relaxation: 0.5, food: 0.3, nature: 0.1, social: 0.9 },
    tags: ['nightlife', 'luxury', 'social'],
  },
  {
    id: 'ancient-ruins',
    title: 'Ancient Ruins',
    image: 'https://images.unsplash.com/photo-1539650116574-8efeb43e2750?w=600&q=80',
    signals: { culture: 0.9, adventure: 0.7, relaxation: 0.2, food: 0, nature: 0.3, social: 0.1 },
    tags: ['history', 'adventure', 'cultural'],
  },
  {
    id: 'cooking-class',
    title: 'Cooking Class',
    image: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&q=80',
    signals: { culture: 0.6, adventure: 0.3, relaxation: 0.3, food: 0.9, nature: 0, social: 0.7 },
    tags: ['food', 'cultural', 'learning'],
  },
];

const MIN_SELECTIONS = 3;
const MAX_SELECTIONS = 5;

// --- DNA Computation ---

export const DIMENSIONS = ['culture', 'adventure', 'relaxation', 'food', 'nature', 'social'];

const PERSONALITY_LABELS = [
  { condition: (d) => d.culture >= 0.6 && d.adventure >= 0.5, label: 'The Cultural Explorer' },
  { condition: (d) => d.adventure >= 0.7 && d.nature >= 0.6, label: 'The Trailblazer' },
  { condition: (d) => d.food >= 0.7 && d.culture >= 0.5, label: 'The Adventurous Foodie' },
  { condition: (d) => d.relaxation >= 0.6 && d.nature >= 0.5, label: 'The Zen Seeker' },
  { condition: (d) => d.social >= 0.7, label: 'The Social Nomad' },
  { condition: (d) => d.culture >= 0.6, label: 'The History Buff' },
  { condition: (d) => d.food >= 0.6, label: 'The Flavor Hunter' },
  { condition: (d) => d.adventure >= 0.6, label: 'The Adrenaline Chaser' },
  { condition: (d) => d.nature >= 0.6, label: 'The Nature Lover' },
  { condition: () => true, label: 'The Curious Wanderer' },
];

export function computeDNA(selectedCards) {
  const raw = {};
  DIMENSIONS.forEach((d) => { raw[d] = 0; });

  selectedCards.forEach((card) => {
    DIMENSIONS.forEach((d) => {
      raw[d] += card.signals[d] || 0;
    });
  });

  // Normalize to 0-1
  const maxVal = Math.max(...Object.values(raw), 1);
  const normalized = {};
  DIMENSIONS.forEach((d) => {
    normalized[d] = Math.round((raw[d] / maxVal) * 100) / 100;
  });

  // Find personality label
  const personality = PERSONALITY_LABELS.find((p) => p.condition(normalized));

  return { dimensions: normalized, personality: personality.label };
}

/**
 * Map DNA dimensions to itinerary generation preferences
 */
export function dnaToPreferences(dimensions, psychographic) {
  const prefs = {
    activityDensity: Math.round((dimensions.adventure * 0.5 + dimensions.social * 0.3 + dimensions.culture * 0.2) * 100),
    pace: Math.round((dimensions.adventure * 0.4 + (1 - dimensions.relaxation) * 0.4 + dimensions.social * 0.2) * 100),
    walkingTolerance: Math.round((dimensions.adventure * 0.5 + dimensions.nature * 0.3 + dimensions.culture * 0.2) * 100),
    historyCulture: Math.round(dimensions.culture * 100),
    nature: Math.round(dimensions.nature * 100),
    foodDining: Math.round(dimensions.food * 100),
    nightlife: Math.round(dimensions.social * 0.5 * 100 + (dimensions.food * 0.2 * 100)),
  };

  // Enrich with psychographic signals when available
  if (psychographic) {
    prefs.budgetLevel = psychographic.comfort > 65 ? 'luxury' : psychographic.comfort > 35 ? 'moderate' : 'budget';
    prefs.localExperiences = psychographic.culture > 60 ? 'deep' : 'standard';
  }

  return prefs;
}

// --- Styled Components ---

const BoardWrapper = styled.div`
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  padding: ${DesignTokens.spacing.xl} ${DesignTokens.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  justify-content: center;

  @media (max-width: 480px) {
    padding: ${DesignTokens.spacing.lg} ${DesignTokens.spacing.md};
  }
`;

const Title = styled(motion.h1)`
  font-size: ${DesignTokens.typography.fontSize.h1};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
  text-align: center;
  margin: 0 0 ${DesignTokens.spacing.xs} 0;
  line-height: ${DesignTokens.typography.lineHeight.heading};

  @media (max-width: 480px) {
    font-size: ${DesignTokens.typography.fontSize.h2};
  }
`;

const Subtitle = styled(motion.p)`
  font-size: ${DesignTokens.typography.fontSize.body};
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin: 0 0 ${DesignTokens.spacing.lg} 0;
  line-height: ${DesignTokens.typography.lineHeight.body};
`;

const SelectionCount = styled(motion.div)`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: ${DesignTokens.spacing.lg};
  text-align: center;

  span {
    color: #4285F4;
    font-weight: ${DesignTokens.typography.fontWeight.semibold};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${DesignTokens.spacing.md};
  width: 100%;
  margin-bottom: ${DesignTokens.spacing.lg};

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 360px) {
    gap: ${DesignTokens.spacing.sm};
  }
`;

const PhotoCard = styled(motion.div)`
  position: relative;
  aspect-ratio: ${DesignTokens.aspectRatio.square};
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${(props) => (props.$selected ? '#4285F4' : 'rgba(255, 255, 255, 0.15)')};
  box-shadow: ${(props) =>
    props.$selected
      ? '0 0 0 2px rgba(66, 133, 244, 0.4), 0 8px 24px rgba(66, 133, 244, 0.3)'
      : GlassmorphismTokens.shadow.glass};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
`;

const PhotoImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${(props) => props.$src});
  background-size: cover;
  background-position: center;
  transition: transform 0.3s ease;

  ${PhotoCard}:hover & {
    transform: scale(1.05);
  }
`;

const PhotoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(0, 0, 0, 0.7) 100%);
`;

const PhotoTitle = styled.span`
  position: absolute;
  bottom: ${DesignTokens.spacing.sm};
  left: ${DesignTokens.spacing.sm};
  right: ${DesignTokens.spacing.sm};
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: white;
  line-height: ${DesignTokens.typography.lineHeight.heading};
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
`;

const CheckBadge = styled(motion.div)`
  position: absolute;
  top: ${DesignTokens.spacing.sm};
  right: ${DesignTokens.spacing.sm};
  width: 28px;
  height: 28px;
  border-radius: ${DesignTokens.borderRadius.full};
  background: #4285F4;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.5);
`;

const CTAWrapper = styled.div`
  width: 100%;
  max-width: 320px;
`;

// --- Component ---

const TravelDNABoard = ({ onComplete, onClose }) => {
  const [selected, setSelected] = useState([]);
  const [dnaResult, setDnaResult] = useState(null);

  const toggleCard = useCallback(
    (card) => {
      setSelected((prev) => {
        const isSelected = prev.find((c) => c.id === card.id);
        if (isSelected) {
          return prev.filter((c) => c.id !== card.id);
        }
        if (prev.length >= MAX_SELECTIONS) return prev;
        return [...prev, card];
      });
    },
    []
  );

  const handleReveal = useCallback(() => {
    const result = computeDNA(selected);
    const prefs = dnaToPreferences(result.dimensions);
    setDnaResult({ ...result, preferences: prefs });

    // Persist to localStorage
    try {
      localStorage.setItem('travelDNA', JSON.stringify({ ...result, preferences: prefs, selectedIds: selected.map((c) => c.id) }));
    } catch {
      // storage full — ignore
    }
  }, [selected]);

  const handleStartPlanning = useCallback(() => {
    if (onComplete && dnaResult) {
      onComplete(dnaResult);
    }
  }, [onComplete, dnaResult]);

  const canReveal = selected.length >= MIN_SELECTIONS;

  if (dnaResult) {
    return (
      <TravelDNAResult
        dimensions={dnaResult.dimensions}
        personality={dnaResult.personality}
        onStartPlanning={handleStartPlanning}
        onBack={() => setDnaResult(null)}
      />
    );
  }

  return (
    <SceneBackground>
      <BoardWrapper>
        <Title
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Your Travel DNA
        </Title>
        <Subtitle
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          Pick 3-5 scenes that speak to you. We will craft your travel personality.
        </Subtitle>

        <SelectionCount>
          <span>{selected.length}</span> / {MAX_SELECTIONS} selected
          {selected.length < MIN_SELECTIONS && ` (pick at least ${MIN_SELECTIONS})`}
        </SelectionCount>

        <Grid>
          {MOOD_CARDS.map((card, i) => {
            const isSelected = selected.some((c) => c.id === card.id);
            const isDisabled = !isSelected && selected.length >= MAX_SELECTIONS;
            return (
              <PhotoCard
                key={card.id}
                $selected={isSelected}
                onClick={() => !isDisabled && toggleCard(card)}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: isDisabled ? 0.5 : 1, scale: 1 }}
                whileTap={!isDisabled ? { scale: 0.95 } : {}}
                transition={{ delay: i * 0.05, duration: 0.3 }}
                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
              >
                <PhotoImage $src={card.image} />
                <PhotoOverlay />
                <PhotoTitle>{card.title}</PhotoTitle>
                <AnimatePresence>
                  {isSelected && (
                    <CheckBadge
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7.5L5.5 11L12 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </CheckBadge>
                  )}
                </AnimatePresence>
              </PhotoCard>
            );
          })}
        </Grid>

        <CTAWrapper>
          <BlueCTAButton
            disabled={!canReveal}
            onClick={handleReveal}
            whileTap={canReveal ? { scale: 0.97 } : {}}
          >
            <span>Reveal My Travel DNA</span>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </BlueCTAButton>
        </CTAWrapper>

        {onClose && (
          <SkipLink
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Skip for now
          </SkipLink>
        )}
      </BoardWrapper>
    </SceneBackground>
  );
};

const SkipLink = styled(motion.button)`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: ${DesignTokens.typography.fontSize.sm};
  cursor: pointer;
  margin-top: ${DesignTokens.spacing.md};
  padding: ${DesignTokens.spacing.sm};
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

export default TravelDNABoard;
